import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";

export default class APIWorkflows extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Get all workflows for the current company
  get = async (
    scope?: types.WorkflowScope,
  ): Promise<types.WorkflowResponse[]> => {
    const url = scope ? `/workflows/?scope=${scope}` : `/workflows/`;
    let res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a new workflow
  post = async (
    workflow: types.WorkflowCreate,
  ): Promise<types.WorkflowResponse> => {
    let res = await this._fetch({
      url: `/workflows/`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: workflow,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get a specific workflow by ID
  getWorkflowId = async (
    workflowId: string,
  ): Promise<types.WorkflowResponse> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a workflow
  putWorkflowId = async (
    workflowId: string,
    workflow: types.WorkflowUpdate,
  ): Promise<types.WorkflowResponse> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: workflow,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete a workflow
  deleteWorkflowId = async (
    workflowId: string,
  ): Promise<{ message: string }> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get all traces for a workflow
  getWorkflowIdTraces = async (
    workflowId: string,
  ): Promise<types.AgentTraceResponse[]> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/traces`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a new trace for a workflow
  postWorkflowIdTraces = async (
    workflowId: string,
    trace: types.AgentTraceCreate,
  ): Promise<types.AgentTraceResponse> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/traces`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: trace,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a trace
  putWorkflowIdTracesTraceId = async (
    workflowId: string,
    traceId: string,
    trace: types.AgentTraceUpdate,
  ): Promise<types.AgentTraceResponse> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/traces/${traceId}`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: trace,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get all available tools
  getTools = async (): Promise<types.ToolInfo[]> => {
    let res = await this._fetch({
      url: `/workflows/available-tools`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Test a tool with parameters
  postToolsTest = async (
    request: types.ToolTestRequest,
  ): Promise<types.ToolTestResponse> => {
    let res = await this._fetch({
      url: `/workflows/test-tool`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: request,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get tools assigned to a workflow
  getWorkflowIdTools = async (
    workflowId: string,
  ): Promise<types.WorkflowToolResponse[]> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/tools`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Set tools for a workflow
  putWorkflowIdTools = async (
    workflowId: string,
    tools: types.WorkflowToolsUpdate,
  ): Promise<types.WorkflowToolResponse[]> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/tools`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: tools,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get all trigger configurations
  getTriggers = async (): Promise<types.WorkflowTriggerConfigResponse[]> => {
    let res = await this._fetch({
      url: `/workflows/trigger-configs`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a trigger configuration
  postTriggers = async (
    trigger: types.WorkflowTriggerConfigCreate,
  ): Promise<types.WorkflowTriggerConfigResponse> => {
    let res = await this._fetch({
      url: `/workflows/trigger-configs`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: trigger,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a trigger configuration
  putTriggerTriggerId = async (
    triggerId: string,
    trigger: types.WorkflowTriggerConfigUpdate,
  ): Promise<types.WorkflowTriggerConfigResponse> => {
    let res = await this._fetch({
      url: `/workflows/trigger-configs/${triggerId}`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: trigger,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete a trigger configuration
  deleteTriggerTriggerId = async (
    triggerId: string,
  ): Promise<{ message: string }> => {
    let res = await this._fetch({
      url: `/workflows/trigger-configs/${triggerId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Execute a workflow with custom input
  postWorkflowIdExecute = async (
    workflowId: string,
    request: types.WorkflowTestRequest,
  ): Promise<types.WorkflowTestResponse> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/execute`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: request,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Copy a workflow to create a personal copy
  postWorkflowIdCopy = async (
    workflowId: string,
    request?: types.WorkflowCopyRequest,
  ): Promise<types.WorkflowResponse> => {
    let res = await this._fetch({
      url: `/workflows/${workflowId}/copy`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: request || {},
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get effective trigger for current user (user-scope if exists, else company-scope)
  getEffectiveTrigger = async (
    sourceType: string,
  ): Promise<types.WorkflowTriggerConfigResponse | null> => {
    let res = await this._fetch({
      url: `/workflows/trigger-configs/effective/${sourceType}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
